import { MetricRecord } from "@/shared/domain/metrics";
import type { FilterDataType } from "@/shared/domain/query/filters";
import { groupKeyLabel } from "@/shared/domain/query/notSet";
import { nsToDateLabel, nsToIso } from "@/shared/time/time";

import { type PlotDatum } from "./MetricChartView";

/** A raw card's plottable series, plus what could not be plotted. */
export interface RawSeries {
  /** One point per session, ordered oldest-first. */
  points: PlotDatum[];
  /** Sessions that matched the window but carried no end time, so there was
   *  nowhere on the time axis to put them. Reported in the card's subtext —
   *  see {@link rawChartSubtext} — rather than silently dropped. */
  undated: number;
}

/**
 * Map raw metric records — one row per session — to a plottable series.
 *
 * Two things this does that the aggregate path does not need to:
 *
 * **Sorts ascending.** The query asks for newest-first, because that is what
 * makes a card's first (and often only) page the CURRENT data rather than the
 * oldest sessions the org ever recorded. A Chart.js line dataset joins points
 * in array order, so the series has to be turned back around before it is
 * drawn — across pages as well as within one.
 *
 * **Drops sessions with no end time.** `max_timestamp_ns` is nullable on the
 * wire even for a row that matched the time filter: the filter runs on the
 * denormalized, COALESCEd column while the response projects the raw session
 * column. Such a row has no x coordinate. It is dropped and counted rather
 * than pinned to `published`, which is when the metric was written — often
 * long after, and sometimes not near at all, the session it describes.
 *
 * No gap-filling: there are no uniform buckets to synthesize, and the linear
 * x-axis already draws the gaps at their real width.
 *
 * **Carries the group through.** A grouped query asks the server to project
 * the group field's value onto every data point
 * (`QueryMetricsRequest.group_by`), exactly as the aggregated path asks it to
 * split its buckets — so the split is a read of `record.group_key`, not a
 * computation. `groupBy` only says WHETHER the points are grouped; the
 * keys themselves come off the wire, which is what lets a raw card group by a
 * custom field the row does not carry.
 */
export function toRawSeries(
  records: MetricRecord[],
  groupBy?: string,
  groupByType?: FilterDataType,
): RawSeries {
  const grouped = groupBy !== undefined;
  const points: PlotDatum[] = [];
  let undated = 0;

  for (const record of records) {
    if (record.max_timestamp_ns === null) {
      undated += 1;
      continue;
    }
    const ns = record.max_timestamp_ns;
    // Chart.js positions on `number`, so the ns bound drops to ms here — the
    // same lossy-but-irrelevant step the aggregate path takes for its labels.
    // The full-precision bigint rides along on start_time/end_time.
    const nsNumber = Number(ns);
    // `null` is the "no value for the field" group — a series of its own,
    // exactly as on the aggregated path, not an absent one.
    const groupKey = record.group_key ?? null;
    points.push({
      start_time: ns,
      end_time: ns,
      value: record.value,
      label: nsToDateLabel(nsNumber),
      tooltipTitle: nsToIso(nsNumber),
      x: nsNumber / 1_000_000,
      session_id: record.session_id,
      ...(grouped
        ? {
            group_key: groupKey,
            group_label: groupKeyLabel(groupKey, groupByType),
          }
        : {}),
    });
  }

  points.sort((a, b) => (a.x ?? 0) - (b.x ?? 0));
  return { points, undated };
}
